import React, { Fragment, useState, useMemo } from "react";
import ProductOption from "components/ProductOption";
import { AUTO_ATTRS } from "components/Form/fields/constants";
import {
  BUTTON_CLASS_CONTEXT,
  INITIAL_LOAD_COUNT,
  UI_THEME
} from "utils/constants/ui";
import SlimCTA from "components/SlimCTA";
import Button from "components/Form/fields/Button";
import { matchSorter } from "match-sorter";
import HoverIconButton from "components/HoverIconButton";
import { prepareProductList } from "utils/product";

const DEFAULT_CLASSES = {
  container: "bg-content--secondary ba hairline-1 br--top br--left br2 h-100",
  inputWrapper: "flex flex-row items-center pa3 bb hairline-1",
  input: "f5 border-box input-reset ba pa2 db w-100 br2 lh-copy b--black-20",
  option: {}
};

const SLIM_CTA_CLASSES = {
  container:
    "w-100 flex flex-column flex-row-ns justify-between pa3 items-start items-center-ns bg-white",
  subheader: "f6"
};

const ProductSearchList = ({
  products,
  prices,
  activeProducts,
  setActiveProduct,
  clearSelections,
  customClasses
}) => {
  const classes = { ...DEFAULT_CLASSES, ...customClasses };
  const [search, setSearch] = useState("");

  const [loadCount, setLoadCount] = useState(INITIAL_LOAD_COUNT);

  const inputProps = {
    className: classes.input,
    type: "text",
    name: "text",
    onChange: (evt) => {
      setSearch(evt.currentTarget.value);
    },
    value: search,
    ...AUTO_ATTRS,
    placeholder: "Search by product name"
  };

  const preparedProducts = useMemo(
    () =>
      prepareProductList({
        products,
        prices
      }),
    [prices.length, products]
  );

  const filtered = matchSorter(preparedProducts, search, {
    keys: ["name"]
  });
  const filteredCount = filtered.length;

  const noneMatchSearch = !filteredCount && search;
  let content;
  if (filteredCount > 0) {
    const disableMore = loadCount >= filteredCount;
    const searchNote = search
      ? `${filteredCount} matches`
      : `${disableMore ? filteredCount : loadCount} of ${filteredCount}`;
    const subset = !search ? filtered.slice(0, loadCount) : filtered;
    content = (
      <Fragment>
        <ul className="list ma0 pa0">
          {subset.map((product, itemIx) => {
            const isFirst = !itemIx;
            const isLast = filteredCount - 1 === itemIx;
            const isActive = activeProducts.indexOf(product.id) > -1;
            const optionsProps = {
              isFirst,
              isLast,
              isActive,
              setActiveProduct,
              product,
              hideActiveBorder: true,
              size: 20
            };
            if (classes.option) {
              optionsProps.customClasses = classes.option;
            }
            return (
              <li key={product.id}>
                <ProductOption {...optionsProps} />
              </li>
            );
          })}
        </ul>
        <div className="pa3 w-100 flex flex-row justify-between items-center">
          <div className="f6">{searchNote}</div>
          <Button
            theme="slim"
            copy="Load all"
            disabled={disableMore}
            classes={{
              contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
            }}
            onClick={() => {
              setLoadCount(products.length);
            }}
          />
        </div>
      </Fragment>
    );
  } else if (noneMatchSearch) {
    content = (
      <SlimCTA
        subheader="No product name matches your search."
        customClasses={SLIM_CTA_CLASSES}
        actions={[
          {
            copy: "Clear",
            onClick: () => setSearch("")
          }
        ]}
      />
    );
  } else {
    content = (
      <SlimCTA
        customClasses={SLIM_CTA_CLASSES}
        subheader="No products available."
      />
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.inputWrapper}>
        <div className="w-100 flex flex-column flex-row-ns items-center">
          <div className="w-100 relative">
            <input {...inputProps} />
            <div
              className="absolute top-0 right-0 bottom-0"
              style={{ paddingTop: 5, paddingRight: 6 }}
            >
              <HoverIconButton onClick={() => setSearch("")} type="refresh" />
            </div>
          </div>
          <div className="dn dib-ns pl2 ml2 bl hairline-1">
            <Button
              copy="Reset"
              onClick={() => {
                clearSelections();
                setSearch("");
              }}
            />
          </div>
          <div className="w-100 dn-ns dib pt2 justify-end flex w-100">
            <Button
              theme={UI_THEME.SLIM}
              copy="Reset"
              disabled={activeProducts.length === 0}
              onClick={() => {
                clearSelections();
                setSearch("");
              }}
            />
          </div>
        </div>
      </div>
      {content}
    </div>
  );
};

export default ProductSearchList;
